import { useMemo } from "react"
import React from "react"
import { Link } from "gatsby"

import { sortCommunities } from "../../utils/communities"
import { readableNumber, unslugify, truncateAtWord } from "../../utils/text"

const TOP_N = 5
const MORE_THRESHOLD = 50

const listSize = (total) => Math.min(TOP_N, Math.max(total, 0))

const andMoreSuffix = (total, shown) => {
  if (!total || total <= shown || total <= MORE_THRESHOLD) return ""
  return `, and ${total - shown} more`
}

const toMembers = (c) => {
  const n = Number(c.members)
  return Number.isFinite(n) && n > 0 ? n : null
}

const hasMemberData = (communities) =>
  communities.some((c) => toMembers(c) != null)

const hasRatingData = (communities) =>
  communities.some((c) => {
    const count = parseInt(c.reviewCount, 10)
    const avg = parseFloat(c.reviewAvg)
    return count > 0 && Number.isFinite(avg)
  })

const hasTagData = (communities) =>
  communities.some((c) => c.tags && String(c.tags).trim())

const platformSlugs = (c) =>
  c.platform
    ? c.platform
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : []

const filterByPlatform = (communities, platformSlug) => {
  if (!platformSlug) return communities
  return communities.filter((c) => platformSlugs(c).includes(platformSlug))
}

const sortByActivity = (communities) => {
  const dated = communities.filter((c) => c.updated)
  if (dated.length) {
    return [...dated].sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )
  }
  return hasRatingData(communities)
    ? sortCommunities("Ratings", [...communities])
    : sortCommunities("Popular", [...communities])
}

const communityLinkClass =
  "text-rose-600 font-medium hover:text-rose-700 underline-offset-2 hover:underline"

const isFreeAccess = c => !c.paid && !c.apply

const joinCommunityNames = communities => {
  const names = communities.map(c => c.name)
  if (!names.length) return ""
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}

const freeCommunityLinks = communities => (
  <ul className="list-disc space-y-1 pl-5 mt-2">
    {communities.map(c => (
      <li key={c.slug}>
        <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
          {c.name}
        </Link>
      </li>
    ))}
  </ul>
)

const communityListElement = (items, renderExtra, totalCount) => {
  const total = totalCount ?? items.length
  const extra = total > MORE_THRESHOLD && items.length < total

  return (
    <ul className="list-disc space-y-2 pl-5">
      {items.map((item) => (
        <li key={item.key}>{item.node}</li>
      ))}
      {extra ? (
        <li className="-ml-5 list-none text-gray-500">
          and {total - items.length} more
        </li>
      ) : null}
      {renderExtra}
    </ul>
  )
}

const reviewScoreSuffix = (c) => {
  const count = parseInt(c.reviewCount, 10)
  const avg = parseFloat(c.reviewAvg)
  if (!count || !Number.isFinite(avg)) return ""
  return ` (${avg.toFixed(1)}/5 from ${count} ${
    count === 1 ? "review" : "reviews"
  })`
}

const ratedCommunityItems = (communities, { withScores = false } = {}) =>
  sortCommunities("Ratings", [...communities])
    .slice(0, listSize(communities.length))
    .map((c) => ({
      key: c.slug,
      name: c.name,
      plainSuffix: withScores
        ? reviewScoreSuffix(c)
        : c.description
          ? `: ${truncateAtWord(c.description, 100)}`
          : "",
      node: (
        <>
          <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
            {c.name}
          </Link>
          {withScores
            ? reviewScoreSuffix(c)
            : c.description
              ? `: ${truncateAtWord(c.description, 100)}`
              : null}
        </>
      ),
    }))

const memberCommunityItems = (communities) =>
  sortCommunities("Largest", [...communities])
    .slice(0, listSize(communities.length))
    .filter((c) => toMembers(c) != null)
    .map((c) => ({
      key: c.slug,
      name: c.name,
      plainSuffix: ` (${readableNumber(toMembers(c))} members)`,
      node: (
        <>
          <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
            {c.name}
          </Link>
          {` (${readableNumber(toMembers(c))} members)`}
        </>
      ),
    }))

const collectPlatformCounts = (communities) => {
  const counts = {}
  communities.forEach((c) => {
    platformSlugs(c).forEach((slug) => {
      counts[slug] = (counts[slug] || 0) + 1
    })
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

const collectTagCounts = (communities) => {
  const counts = {}
  communities.forEach((c) => {
    if (!c.tags) return
    c.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((tag) => {
        counts[tag] = (counts[tag] || 0) + 1
      })
  })
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
}

const collectTopicCounts = (communities) => {
  const counts = {}
  communities.forEach((c) => {
    if (!c.topics) return
    c.topics
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((slug) => {
        counts[slug] = (counts[slug] || 0) + 1
      })
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

const joinPlainList = (items) =>
  items.map((item) => `${item.name}${item.plainSuffix || ""}`).join("; ")

const platformLabel = (platformName, platformGroupName) => {
  if (platformGroupName) return platformGroupName.toLowerCase()
  return `${platformName} communities`.toLowerCase()
}

function buildTopicFaqEntries(communities, pageMetadata) {
  if (!communities?.length) return []

  const { topicName, totalCommunities, slug } = pageMetadata
  const total = totalCommunities ?? communities.length
  const entries = []

  if (hasRatingData(communities)) {
    const items = ratedCommunityItems(communities)
    if (items.length) {
      entries.push({
        question: `What are the best communities for ${topicName}?`,
        answerPlain: `Some of the best ${topicName} communities include ${joinPlainList(
          items
        )}${andMoreSuffix(total, items.length)}.`,
        answerElement: (
          <>
            <p className="mb-2">
              Some of the best {topicName} communities include:
            </p>
            {communityListElement(items, null, total)}
          </>
        ),
      })
    }
  }

  if (hasMemberData(communities)) {
    const items = memberCommunityItems(communities)
    if (items.length) {
      entries.push({
        question: `What are the largest communities for ${topicName}?`,
        answerPlain: `The largest ${topicName} communities include ${joinPlainList(
          items
        )}${andMoreSuffix(total, items.length)}.`,
        answerElement: (
          <>
            <p className="mb-2">The largest {topicName} communities include:</p>
            {communityListElement(items, null, total)}
          </>
        ),
      })
    }
  }

  const platformCounts = collectPlatformCounts(communities)
  if (platformCounts.length) {
    const platformLines = platformCounts.map(
      ([platformSlug, count]) => `${unslugify(platformSlug)} (${count})`
    )
    entries.push({
      question: `What platforms have communities for ${topicName}?`,
      answerPlain: `${topicName} communities appear on ${platformLines.join(
        ", "
      )}.`,
      answerElement: (
        <ul className="list-disc space-y-1 pl-5">
          {platformCounts.map(([platformSlug, count]) => (
            <li key={platformSlug}>
              <Link
                to={`/topics/${slug}/platform/${platformSlug}/`}
                className={communityLinkClass}
              >
                {unslugify(platformSlug)}
              </Link>
              {` (${count} ${count === 1 ? "community" : "communities"})`}
            </li>
          ))}
        </ul>
      ),
    })
  }

  entries.push({
    question: `Are there free communities for ${topicName}?`,
    answerPlain: `Yes, most communities on The Hive Index are free to join. ${topicName} has ${total} communities across ${platformCounts.length} platforms.`,
    answerElement: (
      <p>
        Yes, most communities on The Hive Index are free to join. {topicName}{" "}
        has {total} communities across {platformCounts.length} platforms.
      </p>
    ),
  })

  if (hasMemberData(communities)) {
    const withMembers = communities
      .map((c) => toMembers(c))
      .filter((n) => n != null)
    const memberTotal = withMembers.reduce((sum, n) => sum + n, 0)
    const average = Math.round(memberTotal / withMembers.length)
    entries.push({
      question: `How many members are in ${topicName} communities?`,
      answerPlain: `${topicName} communities on The Hive Index report about ${readableNumber(
        memberTotal
      )} members combined, with an average of about ${readableNumber(
        average
      )} members per community (${withMembers.length} communities with member data).`,
      answerElement: (
        <p>
          {topicName} communities on The Hive Index report about{" "}
          {readableNumber(memberTotal)} members combined, with an average of
          about {readableNumber(average)} members per community (
          {withMembers.length} communities with member data).
        </p>
      ),
    })
  }

  if (hasTagData(communities)) {
    const tagCounts = collectTagCounts(communities)
    if (tagCounts.length) {
      entries.push({
        question: `What are the most common topics in ${topicName} communities?`,
        answerPlain: `Common tags across ${topicName} communities include ${tagCounts
          .map(([tag, count]) => `${tag} (${count})`)
          .join(", ")}.`,
        answerElement: (
          <ul className="list-disc space-y-1 pl-5">
            {tagCounts.map(([tag, count]) => (
              <li key={tag}>
                {tag} ({count})
              </li>
            ))}
          </ul>
        ),
      })
    }
  }

  return entries.slice(0, 6)
}

function buildPlatformTopicFaqEntries(communities, pageMetadata) {
  if (!communities?.length) return []

  const {
    topicName,
    platformName,
    platformSlug,
    platformGroupName,
    totalCommunities,
  } = pageMetadata
  const scoped = filterByPlatform(communities, platformSlug)
  const total = totalCommunities ?? scoped.length
  if (!total) return []

  const label = platformLabel(platformName, platformGroupName)
  const entries = []

  if (hasRatingData(scoped)) {
    const items = ratedCommunityItems(scoped)
    if (items.length) {
      entries.push({
        question: `What are the best ${topicName} ${label}?`,
        answerPlain: `Top ${topicName} ${label} include ${joinPlainList(
          items
        )}${andMoreSuffix(total, items.length)}.`,
        answerElement: (
          <>
            <p className="mb-2">
              Top {topicName} {label} include:
            </p>
            {communityListElement(items, null, total)}
          </>
        ),
      })
    }
  }

  if (hasMemberData(scoped)) {
    const items = memberCommunityItems(scoped)
    if (items.length) {
      entries.push({
        question: `What are the largest ${topicName} ${label}?`,
        answerPlain: `The largest ${topicName} ${label} include ${joinPlainList(
          items
        )}${andMoreSuffix(total, items.length)}.`,
        answerElement: (
          <>
            <p className="mb-2">
              The largest {topicName} {label} include:
            </p>
            {communityListElement(items, null, total)}
          </>
        ),
      })
    }
  }

  entries.push({
    question: `How do I join ${topicName} communities on ${platformName}?`,
    answerPlain: `Browse ${topicName} ${label} on The Hive Index, open a listing you like, and follow its join link to ${platformName}. Most listed communities are free to join. See the ${platformName} platform page for more communities and builder tools.`,
    answerElement: (
      <p>
        Browse {topicName} {label} on The Hive Index, open a listing you like,
        and follow its join link to {platformName}. Most listed communities are
        free to join.{" "}
        <Link to={`/platforms/${platformSlug}/`} className={communityLinkClass}>
          Visit the {platformName} platform page
        </Link>{" "}
        for more communities and builder tools.
      </p>
    ),
  })

  const activeItems = sortByActivity(scoped)
    .slice(0, listSize(scoped.length))
    .map((c) => ({
      key: c.slug,
      name: c.name,
      plainSuffix: "",
      node: (
        <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
          {c.name}
        </Link>
      ),
    }))

  if (activeItems.length) {
    entries.push({
      question: `What are the most active ${topicName} ${label}?`,
      answerPlain: `Active ${topicName} ${label} include ${joinPlainList(
        activeItems
      )}${andMoreSuffix(total, activeItems.length)}.`,
      answerElement: (
        <>
          <p className="mb-2">
            Active {topicName} {label} include:
          </p>
          {communityListElement(activeItems, null, total)}
        </>
      ),
    })
  }

  const withMembers = scoped.map(toMembers).filter((n) => n != null)
  const smallest = withMembers.length
    ? readableNumber(Math.min(...withMembers))
    : null
  const largest = withMembers.length
    ? readableNumber(Math.max(...withMembers))
    : null
  const rangeText =
    smallest && largest
      ? ` ranging from about ${smallest} to ${largest} members`
      : ""

  entries.push({
    question: `How many ${topicName} ${label} are there?`,
    answerPlain: `The Hive Index lists ${total} ${topicName} ${label} on ${platformName}${rangeText}.`,
    answerElement: (
      <p>
        The Hive Index lists {total} {topicName} {label} on {platformName}
        {rangeText ? (
          <>
            , ranging from about {smallest} to {largest} members
          </>
        ) : null}
        .
      </p>
    ),
  })

  return entries.slice(0, 5)
}

function buildPlatformFaqEntries(communities, pageMetadata) {
  if (!communities?.length) return []

  const {
    platformName,
    platformDescription,
    platformGroupNamePlural,
    totalCommunities,
    slug,
    features = [],
    topicRows = [],
    updatedDate,
    siteName = "Hive Index",
  } = pageMetadata
  const total = totalCommunities ?? communities.length
  const communityLabel =
    platformGroupNamePlural || `${platformName} communities`
  const platformIntro = platformDescription
    ? `${platformDescription.trim()}${
        /[.!?]$/.test(platformDescription.trim()) ? "" : "."
      }`
    : `${platformName} hosts online communities for a range of interests.`
  const entries = []

  entries.push({
    question: `What is ${platformName}, and what are ${communityLabel.toLowerCase()} like?`,
    answerPlain: `${platformIntro} ${siteName} organizes ${communityLabel.toLowerCase()} by topic, size, features, and access requirements so you can compare them before joining.`,
    answerElement: (
      <p>
        {platformIntro}{" "}
        {siteName} organizes {communityLabel.toLowerCase()} by topic, size,
        features, and access requirements so you can compare them before
        joining.
      </p>
    ),
  })

  const topTopics = topicRows.slice(0, 5)
  if (topTopics.length) {
    const topicSummary = topTopics
      .map(({ topic, count }) => `${topic.name} (${count})`)
      .join(", ")
    entries.push({
      question: `What topics are most popular on ${platformName}?`,
      answerPlain: `The most common community topics on ${platformName} in the ${siteName} directory are ${topicSummary}.`,
      answerElement: (
        <ul className="list-disc space-y-1 pl-5">
          {topTopics.map(({ topic, count }) => (
            <li key={topic.slug}>
              <Link
                to={`/topics/${topic.slug}/platform/${slug}/`}
                className={communityLinkClass}
              >
                {topic.name}
              </Link>
              {` (${count})`}
            </li>
          ))}
        </ul>
      ),
    })
  }

  const reviewed = communities.filter(c => {
    const count = parseInt(c.reviewCount, 10)
    const avg = parseFloat(c.reviewAvg)
    return count > 0 && Number.isFinite(avg)
  })
  if (reviewed.length >= 2) {
    const items = ratedCommunityItems(reviewed, { withScores: true })
    entries.push({
      question: `What are the best ${platformName} communities to join?`,
      answerPlain: `${joinPlainList(
        items
      )} have the highest review scores among rated ${platformName} listings on ${siteName}. Reviews reflect member experiences, not an editorial ranking.`,
      answerElement: (
        <>
          <p className="mb-2">
            These communities have the highest review scores among rated{" "}
            {platformName} listings on {siteName}:
          </p>
          {communityListElement(items, null, reviewed.length)}
          <p className="mt-2">
            Reviews reflect member experiences, not an editorial ranking.
          </p>
        </>
      ),
    })
  } else if (hasMemberData(communities)) {
    const items = memberCommunityItems(communities)
    entries.push({
      question: `What are the largest ${platformName} communities?`,
      answerPlain: `The largest ${platformName} communities with reported member counts include ${joinPlainList(
        items
      )}${andMoreSuffix(total, items.length)}.`,
      answerElement: (
        <>
          <p className="mb-2">
            The largest {platformName} communities with reported member counts
            include:
          </p>
          {communityListElement(items, null, total)}
        </>
      ),
    })
  }

  const restrictedCount = communities.filter(c => c.apply || c.paid).length
  const freeCount = total - restrictedCount
  const freePercent = Math.round((freeCount / total) * 100)
  const topFreeCommunities = sortCommunities(
    "Popular",
    communities.filter(isFreeAccess)
  ).slice(0, 3)
  const directory = `the ${siteName} directory of communities`
  const freeExamplesPlain = topFreeCommunities.length
    ? ` Popular free examples include ${joinCommunityNames(topFreeCommunities)}.`
    : ""

  let accessIntro
  if (restrictedCount === 0) {
    accessIntro = `All ${total} ${platformName} listings in ${directory} are not marked as paid or application-only.`
  } else if (freeCount === 0) {
    accessIntro = `All ${total} ${platformName} listings in ${directory} require payment, an application, or both.`
  } else {
    accessIntro = `${freeCount} of ${total} (${freePercent}%) ${platformName} listings in ${directory} are not marked as paid or application-only.`
  }

  let accessTail = ""
  if (restrictedCount === 0) {
    accessTail = " Access rules can still change on the community side."
  } else if (freeCount > 0) {
    accessTail = ` The remaining ${restrictedCount} require payment, an application, or both.`
  }

  const accessAnswer = `${accessIntro}${freeExamplesPlain}${accessTail}`

  entries.push({
    question: `Are ${platformName} communities free to join?`,
    answerPlain: accessAnswer,
    answerElement:
      topFreeCommunities.length > 0 && freeCount > 0 ? (
        <>
          <p>{accessIntro} Popular free examples include:</p>
          {freeCommunityLinks(topFreeCommunities)}
          {accessTail ? <p className="mt-2">{accessTail.trim()}</p> : null}
        </>
      ) : (
        <p>{accessAnswer}</p>
      ),
  })

  const topFeatures = features
    .map(feature => ({
      ...feature,
      count: communities.filter(c => c[feature.slug]).length,
    }))
    .filter(feature => feature.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
  if (topFeatures.length) {
    const featureSummary = topFeatures
      .map(
        feature =>
          `${feature.name} (${Math.round((feature.count / total) * 100)}%)`
      )
      .join(", ")
    entries.push({
      question: `What features do ${platformName} communities commonly offer?`,
      answerPlain: `Common features among ${platformName} listings include ${featureSummary}. Use feature filters on ${siteName} to find communities with the features you want.`,
      answerElement: (
        <p>
          Common features among {platformName} listings include{" "}
          {topFeatures.map((feature, index) => (
            <React.Fragment key={feature.slug}>
              {index > 0 &&
                (index === topFeatures.length - 1 ? ", and " : ", ")}
              <Link
                to={`/features/${feature.slug}/`}
                className={communityLinkClass}
              >
                {feature.name}
              </Link>
              {` (${Math.round((feature.count / total) * 100)}%)`}
            </React.Fragment>
          ))}
          . Use feature filters on {siteName} to find communities with the
          features you want.
        </p>
      ),
    })
  }

  entries.push({
    question: `How do I find and join communities on ${platformName}?`,
    answerPlain: `Browse ${platformName} listings in the ${siteName} directory of communities, filter by topic, size, or features, then open a community and follow its join link. Some communities may require an application or paid membership, which is shown on the listing when known.`,
    answerElement: (
      <p>
        Browse {platformName} listings in the {siteName} directory of communities,
        filter by topic, size, or features, then open a community and follow its
        join link. Some communities may require an application or paid membership,
        which is shown on the listing when known.
      </p>
    ),
  })

  const growthAnswer = `List your ${platformName} community on ${siteName} for free to reach people already searching for communities on this platform. Paid promotion can feature your community more prominently across relevant pages and send more qualified visitors to your listing.`
  entries.push({
    question: `How do I grow my community on ${platformName}?`,
    answerPlain: growthAnswer,
    answerElement: (
      <p>
        <Link to="/submit/" className={communityLinkClass}>
          List your {platformName} community for free
        </Link>{" "}
        on {siteName} to reach people already searching for communities on this
        platform.{" "}
        <Link to="/partners/community/" className={communityLinkClass}>
          Paid promotion
        </Link>{" "}
        can feature your community more prominently across relevant pages and
        send more qualified visitors to your listing.
      </p>
    ),
  })

  const updatedCopy = updatedDate
    ? ` This page was last updated ${updatedDate}.`
    : ""
  entries.push({
    question: `How many ${platformName} communities are listed on ${siteName}?`,
    answerPlain: `${siteName} lists ${total} ${communityLabel.toLowerCase()} across ${topicRows.length} topics.${updatedCopy} Listings are updated when communities are submitted, claimed, or reviewed.`,
    answerElement: (
      <p>
        {siteName} lists {total} {communityLabel.toLowerCase()} across{" "}
        {topicRows.length} topics.{updatedCopy} Listings are updated when
        communities are submitted, claimed, or reviewed.
      </p>
    ),
  })

  return entries.slice(0, 8)
}

function useTopicFaqEntries(communities, pageMetadata) {
  return useMemo(
    () => buildTopicFaqEntries(communities, pageMetadata),
    [communities, pageMetadata]
  )
}

function usePlatformTopicFaqEntries(communities, pageMetadata) {
  return useMemo(
    () => buildPlatformTopicFaqEntries(communities, pageMetadata),
    [communities, pageMetadata]
  )
}

function usePlatformFaqEntries(communities, pageMetadata) {
  return useMemo(
    () => buildPlatformFaqEntries(communities, pageMetadata),
    [communities, pageMetadata]
  )
}

export {
  useTopicFaqEntries,
  usePlatformTopicFaqEntries,
  usePlatformFaqEntries,
  buildTopicFaqEntries,
  buildPlatformTopicFaqEntries,
  buildPlatformFaqEntries,
}
